import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosClient from "@/utils/axios-client";
import { toCamelKeyObject, toSnakeKeyObject } from "@/utils/case";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";

// 부티크 회원 정보 — 사업자 정보(사업자등록번호·사업자명·사업자등록증)와 계좌 정보.
// 사업자명·사업자등록번호·계좌는 오더 거래명세서의 FROM·입금계좌에 쓰인다.

export interface BoutiqueBusinessInfo {
  businessRegistrationNumber: string | null;
  businessName: string | null;
  // 등록증 없이 번호만 넣어 둘 수 있어 nullable 이다.
  businessRegistrationCertificate: {
    id: number;
    name: string;
    // presigned inline URL — 수명이 짧다.
    url: string;
    registeredAt: string | null;
  } | null;
}

// 보낸 항목만 갱신된다(미전달 = 기존 값 유지).
export interface BoutiqueBusinessInfoRequestBody {
  businessRegistrationCertificateFileId?: number | null;
  businessRegistrationNumber?: string | null;
  businessName?: string | null;
}

export interface BoutiqueBankInfo {
  id: number;
  alias: string;
  bankName: string;
  // 계좌번호 — 국내 계좌도 이 칸에 넣는다.
  iban: string;
  swiftCode: string;
  branchName: string;
  branchAddress: string;
}

export type BoutiqueBankInfoRequestBody = Omit<BoutiqueBankInfo, "id">;

const url = {
  businessInfo: (boutiqueId: number) =>
    `/boutiques/${boutiqueId}/business-info`,
  bankInfoList: (boutiqueId: number) => `/boutiques/${boutiqueId}/bank_info`,
  bankInfo: (boutiqueId: number, bankInfoId: number) =>
    `/boutiques/${boutiqueId}/bank_info/${bankInfoId}`,
};

const businessInfoKey = (boutiqueId?: number) => [
  "boutique-business-info",
  boutiqueId,
];
const bankInfoListKey = (boutiqueId?: number) => [
  "boutique-bank-info-list",
  boutiqueId,
];

export function useGetBoutiqueBusinessInfo() {
  const { companyId } = useCompanyIdentity();
  return useQuery({
    queryKey: businessInfoKey(companyId),
    queryFn: async () => {
      const { data } = await axiosClient.get(
        url.businessInfo(companyId as number)
      );
      return toCamelKeyObject(data) as BoutiqueBusinessInfo;
    },
    enabled: Boolean(companyId),
  });
}

export function useUpdateBoutiqueBusinessInfo() {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { error } = useAppToasts({ id: "GLOBAL" });
  return useMutation({
    mutationFn: (body: BoutiqueBusinessInfoRequestBody) =>
      axiosClient.put(
        url.businessInfo(companyId as number),
        toSnakeKeyObject(body)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: businessInfoKey(companyId) }),
    onError: (err) => error(err),
  });
}

export function useListBoutiqueBankInfo() {
  const { companyId } = useCompanyIdentity();
  return useQuery({
    queryKey: bankInfoListKey(companyId),
    queryFn: async () => {
      const { data } = await axiosClient.get(
        url.bankInfoList(companyId as number),
        { params: { page_number: 1, page_size: 100 } }
      );
      const list = (toCamelKeyObject(data)?.bankInfoList ??
        []) as BoutiqueBankInfo[];
      // 거래명세서에는 가장 먼저 등록한 계좌가 찍히므로 등록 순으로 보여준다.
      return [...list].sort((a, b) => a.id - b.id);
    },
    enabled: Boolean(companyId),
  });
}

export function useSaveBoutiqueBankInfo() {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { error } = useAppToasts({ id: "GLOBAL" });
  return useMutation({
    // id 가 있으면 수정, 없으면 새로 등록한다.
    mutationFn: ({
      id,
      body,
    }: {
      id?: number;
      body: BoutiqueBankInfoRequestBody;
    }) =>
      id
        ? axiosClient.put(
            url.bankInfo(companyId as number, id),
            toSnakeKeyObject(body)
          )
        : axiosClient.post(
            url.bankInfoList(companyId as number),
            toSnakeKeyObject(body)
          ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: bankInfoListKey(companyId) }),
    onError: (err) => error(err),
  });
}

export function useDeleteBoutiqueBankInfo() {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { error } = useAppToasts({ id: "GLOBAL" });
  return useMutation({
    mutationFn: (id: number) =>
      axiosClient.delete(url.bankInfo(companyId as number, id)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: bankInfoListKey(companyId) }),
    onError: (err) => error(err),
  });
}
